
import React, { useState, useEffect, useCallback } from 'react';
import { CountryCode, Identity, GenerationConfig, User } from './types';
import { generateIdentityWithAI } from './services/geminiService';
import { generateIdentityLocally } from './services/localService';
import { historyService } from './services/historyService';
import { authService } from './services/authService';
import { translations, Language } from './translations';
import IdentityCard from './components/IdentityCard';
import BulkExtract from './components/BulkExtract';
import HelpGuide from './components/HelpGuide';
import SystemSettings from './components/SystemSettings';
import AdminDashboard from './components/AdminDashboard';
import AuthModal from './components/AuthModal';
import DisclaimerModal from './components/DisclaimerModal';
import { 
  Zap, Globe, Settings, History, RefreshCw, 
  Sparkles, LayoutDashboard, Database, BookOpen, 
  ShieldCheck, LogIn, User as UserIcon, LogOut
} from 'lucide-react';

const App: React.FC = () => {
  const [lang] = useState<Language>('zh');
  const [user, setUser] = useState<User | null>(authService.getCurrentUser());
  const [loading, setLoading] = useState(false);
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [history, setHistory] = useState<Identity[]>([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isLegalAccepted, setIsLegalAccepted] = useState(false);
  
  const t = translations[lang];

  const [config, setConfig] = useState<GenerationConfig>({
    country: CountryCode.US,
    gender: '随机',
    minAge: 22,
    maxAge: 45,
    model: 'gemini-3-flash-preview'
  });

  const syncHistory = useCallback(async (username: string) => {
    try {
      const res = await historyService.fetchHistory(username);
      if (res.success) {
        setHistory(res.history);
        if (res.history.length > 0 && !identity) {
          setIdentity(res.history[0]);
        }
      }
    } catch (e) {
      if (e instanceof Error && e.message === "SESSION_EXPIRED") {
        authService.logout();
        setUser(null);
      }
    }
  }, [identity]);

  useEffect(() => {
    if (user && isLegalAccepted) {
      syncHistory(user.username);
    }
  }, [user, isLegalAccepted, syncHistory]);

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setHistory([]);
    setIdentity(null);
    setActiveTab('dashboard');
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const result = await generateIdentityWithAI(config);
      setIdentity(result);
      if (user) {
        await historyService.saveHistory(user.username, result);
        syncHistory(user.username);
      } else {
        setHistory(prev => [result, ...prev].slice(0, 5));
      }
    } catch (e) {
      const local = await generateIdentityLocally(config);
      setIdentity(local);
    } finally {
      setLoading(false);
    }
  };

  const NavItem = ({ id, icon: Icon, label }: any) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all duration-200 ${
        activeTab === id ? 'bg-blue-50 text-blue-900 shadow-sm border border-blue-200' : 'text-slate-800 hover:bg-slate-100'
      }`}
    >
      <Icon size={20} />
      <span className="capitalize">{label}</span>
    </button>
  );

  return (
    <div className="flex min-h-screen bg-[#F8FAFC] antialiased overflow-x-hidden">
      <DisclaimerModal 
        lang={lang} 
        onAccept={() => setIsLegalAccepted(true)} 
        onReject={() => window.location.href = 'https://google.com'} 
      />
      
      <AuthModal 
        isOpen={isAuthOpen} 
        onClose={() => setIsAuthOpen(false)} 
        onSuccess={(u) => { setUser(u); syncHistory(u.username); }} 
      />

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-72 flex-col fixed inset-y-0 bg-white border-r border-slate-200 z-50">
        <div className="p-8 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-800 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
            <Zap size={24} fill="currentColor" />
          </div>
          <h1 className="font-black text-xl tracking-tight text-slate-900">Identity Pro</h1>
        </div>
        
        <nav className="flex-1 px-4 space-y-1">
          <NavItem id="dashboard" icon={LayoutDashboard} label={t.dashboard} />
          <NavItem id="extract" icon={Database} label={t.extract} />
          <NavItem id="help" icon={BookOpen} label={t.help} />
          {user?.role === 'admin' && <NavItem id="admin" icon={ShieldCheck} label={t.admin} />}
          <NavItem id="settings" icon={Settings} label={t.settings} />
        </nav>

        <div className="p-4 border-t border-slate-100">
          {user ? (
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl border border-slate-300">
              <div className="w-10 h-10 rounded-xl bg-blue-800 text-white flex items-center justify-center font-bold">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="text-xs font-black text-slate-900 truncate">{user.username}</p>
                <p className="text-[10px] text-blue-800 font-bold uppercase tracking-wider">{user.role}</p>
              </div>
              <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-red-600 transition-colors" title="登出系统">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setIsAuthOpen(true)}
              className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-2 hover:bg-blue-800 transition-all shadow-lg"
            >
              <LogIn size={16} /> {t.login}
            </button>
          )}
        </div>
      </aside>

      <main className="flex-1 lg:ml-72 min-h-screen content-container-stable" style={{ contain: 'layout' }}>
        {/* Responsive Header */}
        <header className="h-20 px-4 md:px-8 flex items-center justify-between sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
          <div className="flex items-center gap-3">
            {/* Mobile Logo: visible only on mobile since sidebar is hidden */}
            <div className="lg:hidden w-8 h-8 bg-blue-800 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
              <Zap size={18} fill="currentColor" />
            </div>
            <h2 className="text-lg font-extrabold text-slate-900 truncate">{(t as any)[activeTab]}</h2>
          </div>

          <div className="flex items-center gap-3">
             {user ? (
               <div className="flex items-center gap-3">
                 <div className="hidden sm:flex items-center bg-slate-100 rounded-xl px-3 py-1.5 border border-slate-300">
                   <span className="text-[10px] font-black text-slate-800 uppercase mr-2">{t.quota}:</span>
                   <span className="text-xs font-bold text-blue-800">{user.used_quota} / {user.quota}</span>
                 </div>
                 {/* Mobile User Trigger: Click to go to settings/logout */}
                 <button 
                   onClick={() => setActiveTab('settings')}
                   className="lg:hidden w-10 h-10 rounded-xl bg-blue-800 text-white flex items-center justify-center font-bold text-sm shadow-md active:scale-95 transition-transform"
                 >
                   {user.username.charAt(0).toUpperCase()}
                 </button>
               </div>
             ) : (
               /* Mobile Login Button: missing in previous version */
               <button 
                 onClick={() => setIsAuthOpen(true)}
                 className="lg:hidden px-4 py-2 bg-slate-900 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-2 hover:bg-blue-800 transition-all shadow-lg active:scale-95"
               >
                 <LogIn size={16} /> {t.login}
               </button>
             )}
          </div>
        </header>

        <div className="max-w-6xl mx-auto p-4 md:p-8">
          {activeTab === 'dashboard' && (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 animate-in fade-in duration-500">
              <div className="xl:col-span-4 space-y-6">
                <section className="bg-white p-6 rounded-[2rem] border border-slate-300 shadow-sm transition-shadow hover:shadow-md min-h-[400px]">
                  <h2 className="font-bold text-slate-900 flex items-center gap-2 mb-6">
                    <Settings size={18} className="text-blue-700" /> {t.config}
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold text-slate-800 uppercase tracking-widest">{t.country}</label>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.keys(CountryCode).map((code) => (
                          <button
                            key={code}
                            onClick={() => setConfig({ ...config, country: (CountryCode as any)[code] })}
                            className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${
                              config.country === (CountryCode as any)[code]
                                ? 'border-blue-800 bg-blue-50 text-blue-900 ring-2 ring-blue-100'
                                : 'border-slate-300 bg-slate-50 text-slate-900 hover:bg-slate-100'
                            }`}
                          >
                            {(t.countries as any)[(CountryCode as any)[code]]}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={handleGenerate}
                      disabled={loading}
                      aria-busy={loading}
                      className="w-full py-4 bg-blue-800 text-white rounded-2xl font-black text-sm flex items-center justify-center gap-3 shadow-xl shadow-blue-200 hover:bg-blue-900 active:scale-95 transition-all disabled:opacity-50"
                    >
                      {loading ? <RefreshCw className="animate-spin" size={18} /> : <Sparkles size={18} />}
                      {loading ? t.generating : t.generate}
                    </button>
                  </div>
                </section>

                <section className="bg-white p-6 rounded-[2rem] border border-slate-300 shadow-sm min-h-[300px]">
                  <h2 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <History size={18} className="text-slate-700" /> {t.history}
                  </h2>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                    {history.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <History size={32} className="text-slate-300" />
                        <p className="text-[10px] font-bold uppercase mt-2 text-slate-700">{t.noHistory}</p>
                      </div>
                    ) : (
                      history.map((h, i) => (
                        <button
                          key={h.id || i}
                          onClick={() => setIdentity(h)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                            identity?.id === h.id ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-300' : 'hover:bg-slate-50 border-transparent'
                          } border`}
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center font-bold text-xs text-slate-800 aspect-square">
                            {h.name.charAt(0)}
                          </div>
                          <div className="text-left overflow-hidden">
                            <p className="text-xs font-bold text-slate-900 truncate">{h.name}</p>
                            <p className="text-[10px] text-slate-700 truncate">{h.job}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </section>
              </div>

              <div className="xl:col-span-8">
                {identity ? (
                  <IdentityCard identity={identity} lang={lang} />
                ) : (
                  <div className="bg-white h-[600px] rounded-[2.5rem] border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-center p-12 transition-colors hover:border-blue-300">
                     <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6 shadow-inner border border-slate-200">
                       <Globe size={40} className="text-slate-400" />
                     </div>
                     <h3 className="text-2xl font-black text-slate-900 mb-2">{t.welcome}</h3>
                     <p className="text-slate-800 max-w-sm leading-relaxed text-sm">{t.welcomeDesc}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'extract' && <BulkExtract history={history} lang={lang} onUpdateHistory={() => user && syncHistory(user.username)} />}
          {activeTab === 'help' && <HelpGuide />}
          {activeTab === 'admin' && <AdminDashboard />}
          {activeTab === 'settings' && <SystemSettings user={user} onLogout={handleLogout} onClearHistory={() => { setHistory([]); setIdentity(null); }} />}
        </div>
      </main>
    </div>
  );
};

export default App;
